/**
 * Ranked horizontal bar list — the readable replacement for top-N bar
 * charts and share doughnuts on the Spend dashboard.
 *
 * Each row: rank, full name (no angled/truncated axis labels), a bar sized
 * against the largest row, and the £ value with its share of the shown
 * total. Long supplier / part names get the whole row width, which is what
 * the vertical charts could never give them.
 *
 * @param rows - resolver data points ({name, value}); rendered sorted by value
 * @param color - bar fill colour (one accent per card, not a rainbow)
 * @param formatValue - value formatter (e.g. compact £)
 * @param maxRows - cap on rows shown (remainder rolls into an "Other" row)
 */

interface RankedRow {
  name: string;
  value?: number;
  /** Secondary label (e.g. the part number under its description). */
  sub?: string;
}

interface RankedBarListProps {
  rows: RankedRow[] | undefined;
  color: string;
  formatValue: (value: number) => string;
  maxRows?: number;
  /** Height of the pulse placeholder while rows are undefined. */
  skeletonRows?: number;
}

export default function RankedBarList({
  rows,
  color,
  formatValue,
  maxRows = 10,
  skeletonRows = 8,
}: RankedBarListProps) {
  if (rows === undefined) {
    return (
      <div className="space-y-3 py-1">
        {Array.from({ length: skeletonRows }, (_, i) => (
          <div key={i} className="h-6 animate-pulse rounded bg-muted" />
        ))}
      </div>
    );
  }

  // A payload may carry its own "Other" rollup row (the pie resolver does).
  // That row is context, not a competitor — it never takes a rank, and sits
  // greyed at the bottom together with anything past the row cap.
  const all = rows.map((r) => ({ name: r.name, sub: r.sub, value: r.value || 0 }));
  const named = all.filter((r) => !/^other\b/i.test(r.name))
    .sort((a, b) => b.value - a.value);
  const payloadOther = all
    .filter((r) => /^other\b/i.test(r.name))
    .reduce((s, r) => s + r.value, 0);
  const shown: Array<{ name: string; sub?: string; value: number; isOther?: boolean }> =
    named.slice(0, maxRows);
  const rest = named.slice(maxRows);
  const restTotal = rest.reduce((s, r) => s + r.value, 0) + payloadOther;
  if (restTotal > 0) {
    shown.push({
      name: rest.length ? `Other (${rest.length}+)` : 'Other',
      value: restTotal,
      isOther: true,
    });
  }
  const total = all.reduce((s, r) => s + r.value, 0);
  const max = shown.length ? Math.max(...shown.map((r) => r.value)) : 0;

  if (!shown.length || total <= 0) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
        No invoiced spend in the current scope.
      </div>
    );
  }

  return (
    <div className="space-y-2.5 py-1">
      {shown.map((row, i) => {
        const isOther = Boolean(row.isOther);
        const pct = total ? (row.value / total) * 100 : 0;
        const width = max ? Math.max((row.value / max) * 100, 1.5) : 0;
        return (
          <div key={`${row.name}|${row.sub || ''}`} className="flex items-center gap-3">
            <span className="w-5 shrink-0 text-right text-[11px] tabular-nums text-slate-400">
              {isOther ? '' : i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex items-baseline justify-between gap-3">
                <span
                  className="min-w-0 truncate"
                  title={row.sub ? `${row.name} (${row.sub})` : row.name}
                >
                  <span className={`text-xs font-medium ${isOther ? 'text-slate-400' : 'text-slate-700'}`}>
                    {row.name}
                  </span>
                  {row.sub && (
                    <span className="ml-1.5 text-[10px] text-slate-400">{row.sub}</span>
                  )}
                </span>
                <span className="shrink-0 text-[11px] tabular-nums text-slate-500">
                  <span className="font-semibold text-slate-900">{formatValue(row.value)}</span>
                  {' · '}{pct >= 10 ? Math.round(pct) : pct.toFixed(1)}%
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full transition-[width] duration-300"
                  style={{ width: `${width}%`, background: isOther ? '#cbd5e1' : color }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
